/* eslint-disable no-console */
import { push } from 'connected-react-router';
import get from 'lodash.get';
import * as actions from './actions';
import SuccessToast from '../../../shared/components/Toasts/SuccessToast';
import ErrorToast from '../../../shared/components/Toasts/ErrorToast';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../constants';
import AccountApi from '../../../shared/apis/AccountApi';
import ContactsApi from '../../../shared/apis/ContactsApi';
import { KissMetrics } from '../../../shared/services';
import MyCarsApi from '../../../shared/apis/MyCarsApi';
import type { AppDispatch } from '../../../store/types';

export const fetchPurchases = () => (dispatch: AppDispatch) => {
  dispatch(actions.fetchPurchasesBegin());
  return MyCarsApi.getPurchases()
    .then((response) => {
      dispatch(actions.fetchPurchasesSuccess(response.data));
    })
    .catch((error) => {
      dispatch(actions.fetchPurchasesFailure(error));
    });
};

export const fetchPersonalDetails = () => (dispatch: AppDispatch) => {
  dispatch(actions.fetchPersonalDetailsBegin());
  return AccountApi.getPersonalDetails()
    .then((response) => {
      dispatch(actions.fetchPersonalDetailsSuccess(response.data));
    })
    .catch((error) => {
      dispatch(actions.fetchPersonalDetailsFailure(error));

      const isTimeout = get(error, 'response.status') !== 500;
      if (isTimeout) {
        ErrorToast(ERROR_MESSAGES.GET_PERSONAL_DETAILS_TIMEOUT);
      }
    });
};

export const fetchUserProfileData = (userId: string) => (dispatch: AppDispatch) => {
  dispatch(actions.fetchUserProfileDataBegin());
  return AccountApi.getUserProfileData(userId)
    .then((response) => {
      dispatch(actions.fetchUserProfileDataSuccess(response.data.data));
    })
    .catch((error) => {
      dispatch(actions.fetchUserProfileDataFailure(error));

      const isTimeout = get(error, 'response.status') !== 500;
      if (isTimeout) {
        ErrorToast(ERROR_MESSAGES.GET_USER_PROFILE_DATA_TIMEOUT);
      }
    });
};

const getPersonalInformationErrorToast = (error: unknown) => {
  const status = get(error, 'response.status');
  switch (true) {
    case !status || status >= 500:
      return ERROR_MESSAGES.UPDATE_PERSONAL_INFORMATION;
    case status === 400:
      return get(error, 'response.data', ERROR_MESSAGES.UPDATE_PERSONAL_INFORMATION);
    default:
      return ERROR_MESSAGES.UPDATE_PERSONAL_INFORMATION_TIMEOUT;
  }
};

const getUserProfileDataErrorToast = (error: unknown) => {
  const status = get(error, 'response.status');
  switch (true) {
    case !status || status >= 500:
      return ERROR_MESSAGES.UPDATE_USER_PROFILE_DATA;
    case status === 400:
      return get(error, 'response.data', ERROR_MESSAGES.UPDATE_USER_PROFILE_DATA);
    default:
      return ERROR_MESSAGES.UPDATE_USER_PROFILE_DATA_TIMEOUT;
  }
};

const getAddressErrorToast = (error: unknown) => {
  const isInternalServerError = get(error, 'response.status') === 500;
  const isUnverifiedAddressError = get(error, 'response.status') === 400;

  if (isUnverifiedAddressError) return ERROR_MESSAGES.UPDATE_ADDRESS_UNVERIFIED;
  if (isInternalServerError) return ERROR_MESSAGES.UPDATE_ADDRESS;
  return ERROR_MESSAGES.UPDATE_ADDRESS_TIMEOUT;
};

export const updatePersonalInformation =
  (
    userPersonalInformation: Record<string, unknown>,
    sectionName: string,
    refreshProfile: () => void
  ) =>
  (dispatch: AppDispatch) => {
    const informationUpdated = Object.keys(userPersonalInformation);
    dispatch(actions.updatePersonalInformationBegin());
    return AccountApi.updatePersonalInformation(userPersonalInformation)
      .then((response) => {
        dispatch(actions.updatePersonalInformationSuccess(response));
        dispatch(fetchPersonalDetails());
        dispatch(push('/personal-details'));
        SuccessToast(SUCCESS_MESSAGES.UPDATE_PERSONAL_INFORMATION);
        refreshProfile();
        KissMetrics.trackEvent('Account - Updated Personal Details', {
          SectionName: sectionName,
          InformationUpdated: informationUpdated,
          WasSuccessful: true
        });
      })
      .catch((error) => {
        console.error(error);
        dispatch(actions.updatePersonalInformationFailure(error));
        KissMetrics.trackEvent('Account - Updated Personal Details', {
          SectionName: sectionName,
          InformationUpdated: informationUpdated,
          WasSuccessful: false
        });

        const errorToastMessage = getPersonalInformationErrorToast(error);

        ErrorToast(errorToastMessage);
      });
  };

export const updateUserProfileData =
  (userId: string, preferredName: string, preferredLanguage: string) => (dispatch: AppDispatch) => {
    dispatch(actions.updateUserProfileDataBegin());
    return AccountApi.updateUserProfileData(userId, preferredName, preferredLanguage)
      .then((response) => {
        dispatch(actions.updateUserProfileDataSuccess(response));
        dispatch(fetchPersonalDetails());
        dispatch(push('/personal-details'));
        SuccessToast(SUCCESS_MESSAGES.UPDATE_USER_PROFILE_DATA);
      })
      .catch((error) => {
        console.error(error);
        dispatch(actions.updateUserProfileDataFailure(error));

        const errorToastMessage = getUserProfileDataErrorToast(error);

        ErrorToast(errorToastMessage);
      });
  };

export const updateContactsDetails =
  (userContactsDetails: Record<string, unknown>, refreshProfile: () => void) =>
  (dispatch: AppDispatch) => {
    dispatch(actions.updateContactsDetailsBegin());
    return ContactsApi.updateContactsDetails(userContactsDetails)
      .then(() => {
        dispatch(actions.updateContactsDetailsSuccess());
        dispatch(fetchPersonalDetails()).then(() => {
          dispatch(push('/personal-details'));
          SuccessToast(SUCCESS_MESSAGES.UPDATE_PERSONAL_INFORMATION);

          refreshProfile();
        });
      })
      .catch((error) => {
        console.error(error);
        dispatch(actions.updateContactsDetailsFailure(error));
        // note(oscar): it looks like we're displaying a generic message when UO fails.
        ErrorToast(ERROR_MESSAGES.UPDATE_CONTACT_DETAILS_ERROR);
      });
  };

export const updateAddress =
  (
    address: Record<string, unknown>,
    sectionName: string,
    trackEvent: (...args: any[]) => void,
    isOrderPlaced: boolean
  ) =>
  (dispatch: AppDispatch) => {
    const informationUpdated = Object.keys(address);
    dispatch(actions.updateAddressBegin());
    return AccountApi.updateAddress(address)
      .then(() => {
        dispatch(actions.updateAddressSuccess());
        dispatch(fetchPersonalDetails());
        dispatch(push('/personal-details'));
        SuccessToast(SUCCESS_MESSAGES.UPDATE_ADDRESS);
        KissMetrics.trackEvent('Account - Updated Personal Details', {
          SectionName: sectionName,
          InformationUpdated: informationUpdated,
          WasSuccessful: true
        });
        if (isOrderPlaced && sectionName === 'Edit Home Address') {
          trackEvent('Account - Edit Home Address - Home Address edited while in order', {
            SectionName: sectionName,
            InformationUpdated: informationUpdated,
            WasSuccessful: true
          });
        }
      })
      .catch((error) => {
        console.error(error);
        dispatch(actions.updateAddressFailure());
        KissMetrics.trackEvent('Account - Updated Personal Details', {
          SectionName: sectionName,
          InformationUpdated: informationUpdated,
          WasSuccessful: false
        });

        const errorToastMessage = getAddressErrorToast(error);

        ErrorToast(errorToastMessage);
      });
  };
